import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { useJSONState, useLogout, useTranslate } from '../hooks';
import "../assets/style/Shipments.css"
import { TextField, Button, ConfirmDialog, Grid, Container, Group, Centralizer, Switch, Select, DataTable, Form, Popover, Swap } from '../components';
import { ShipmentType } from '../types/Shipments';
import { ConfirmDialogProps } from '../components/Utils/ConfirmDialog';
import Shipment from './Shipment';
import { format, set } from 'date-fns';
import { toast } from 'sonner';
import { deepCopyOf, dialog, sleep, Dialog } from '../utils';
import { FaCircleInfo, FaArrowRight } from "react-icons/fa6";
import DeliveryTypes from './DeliveryTypes';
import { CarrierType, DeliveryTypeType } from '../types';
import { axiosPrivate } from '../api/axios';

type statusSearch = {
  [key: string]: string
}

const EOD_LABEL_OPTIONS = ["unprinted", "forcePrint", "noPrint"] as const
const EOD_BOOK_OPTIONS = ["unprinted", "noPrint"] as const
const EOD_WAYBILL_OPTIONS = ["unprinted", "forcePrint", "noPrint"] as const
const EOD_SHIPMENT_LIST_OPTIONS = ["unprinted", "forcePrint", "noPrint"] as const

export default function Shipments() {
  // Hooks
  const api = useAxiosPrivate();
  const logout = useLogout();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const [statuses, setStatuses] = useState<statusSearch[]>([])
  const [triggerReload, setTriggerReload] = useState(false);
  const [eodMode, setEODMode] = useState<"off" | "on" | "dialog">("off");
  const [showSearchTips, setShowSearchTips] = useState<boolean>(false);
  const [eod_Labels, set_eod_Labels] = useState<typeof EOD_LABEL_OPTIONS[number]>("noPrint");
  const [eod_Waybills, set_eod_Waybills] = useState<typeof EOD_WAYBILL_OPTIONS[number]>("unprinted");
  const [eod_Book, set_eod_Book] = useState<typeof EOD_BOOK_OPTIONS[number]>("unprinted");
  const [eod_ShipmentList, set_eod_ShipmentList] = useState<typeof EOD_SHIPMENT_LIST_OPTIONS[number]>("unprinted");
  const [deliveryTypes, setDeliveryTypes] = useState<Pick<DeliveryTypeType, "id" | "name">[]>([])
  const [carriers, setCarriers] = useState<Pick<CarrierType, "name" | "carrierId">[]>([])
  const PAGE_SIZE = 50;
  const shipmentIds = useRef<string[]>([])

  // States
  const [newShipmentForm, setNewShipmentForm] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogProps>();
  const [showInterval, setShowInterval] = useState(false);
  const [searchFields, setSearchField, setSearchFields] = useJSONState<{
    trackingNo?: string,
    orderNumber?: string,
    receiver?: string,
    dispDate?: string,
    carrier?: string,
    deliveryType?: string,
    status?: string,
    shipmentId?: string,
    dispDateFrom?: string,
    dispDateTo?: string,
  }>({
    trackingNo: undefined,
    orderNumber: undefined,
    receiver: undefined,
    dispDate: undefined,
    carrier: undefined,
    deliveryType: undefined,
    status: undefined,
    shipmentId: undefined,
    dispDateFrom: undefined,
    dispDateTo: undefined,
  })
  // Defaults
  const dialogDefault = {
    title: t("NoShipmentFound"),
    message: t("VerifySearchConditions"),
    noCancel: true,
    type: "info",
    onOk: () => { setConfirmDialog(undefined) }
  } as ConfirmDialogProps

  const searchShipment = async (e: any, initial: boolean = false) => {
    if (e) e.preventDefault()
    let searchData = {} as any;

    if (initial) {
      let sStorage = sessionStorage.getItem("searchFields");
      searchData = JSON.parse(sStorage ?? "{}")
      if (Object.keys(searchData).length == 0) return
      setSearchFields({ ...searchData })
      if (searchData.dispDateFrom || searchData.dispDateTo) setShowInterval(true)
      return
    }
    sessionStorage.setItem("searchFields", JSON.stringify(searchFields ?? {}))
    setTriggerReload((v) => !v)
  }

  const fetchShipments = async ({ page, snapshot, pageSize }: {
    page: number,
    snapshot: string,
    pageSize: number,
  }) => {
    try {
      let searchData = {} as any;
      let sStorage = sessionStorage.getItem("searchFields");
      searchData = JSON.parse(sStorage ?? "{}")

      const preDate = (new Date()).getTime()
      const response = await api.post({
        endpoint: "/ShipmentsList",
        data: { searchFields: searchData, page, snapshot, pageSize },
        mount: null
      })

      // For the search we want an indication of search. But not for page switch
      if (snapshot === "") {
        const afterDate = (new Date()).getTime()
        if (afterDate - preDate < 400) await sleep(400 - (afterDate - preDate))
      }

      let data = response?.data?.data

      if (!data) {
        //toast.info(t("NoShipmentFound") + ".\n" + t("VerifySearchConditions"))
        return Promise.resolve({ data: [] as ShipmentType[], snapshot: "", pages: 0 })
      }

      const pages = response.data.pages
      snapshot = response.data.snapshot

      data = data.reduce((prev: any, cur: ShipmentType) => {
        const rec = cur.addresses.receiver
        const foo = {
          "CarrierSuite": (cur.carrierSuiteId != "STANDARD" ?
            <img src={`/images/carriers/${cur.carrierSuiteId}.png`} alt="Carrier logotype" /> :
            <div style={{ fontFamily: "Monospace", fontSize: "1.1rem", fontWeight: 600, letterSpacing: 1.2 }}>{cur.carrier}</div>
          ),
          orderNumber: cur.orderNumber,
          receiver: `${rec.name}\n${rec.street1}\n${rec.countryCode}-${rec.zipCode} ${rec.city}`,
          dispDate: cur.dispDate,
          deliveryType: cur.deliveryTypeName,
          status: <div className={getStatusElementClass(cur.status)}><p className='m-auto'>{t(`ta_statuses.${cur.status}`)}</p></div>,
          trackingNumber: cur.trackingLink ? <a className="tracking-number-link" title={t("track")} href={cur.trackingLink} target='_blank'>{cur.trackingNumber ?? t("track")}</a> :
            cur.trackingNumber ? <span>{cur.trackingNumber}</span> : "",
          noteInternal: !!cur.noteInternal ? <FaCircleInfo /> : null,
          shipmentId: cur.shipmentId
        }
        prev.push(foo)
        return prev
      }, [])

      return Promise.resolve({ data: data as ShipmentType[], snapshot, pages })
    }
    catch (e) {
      if (e?.response?.status !== 401) console.error(e)
      return Promise.resolve({ data: [] as ShipmentType[], snapshot, pages: 0 })
    }
  }


  const endOfDay = async () => {
    let title = t("eod.confirm").replace("{1}", shipmentIds.current.length.toString())

    const res = await dialog.open({}, { title })

    if (!res) return Promise.resolve()

    try {
      await api.post({
        endpoint: "shipments/print",
        mount: {
          loadingText: t("printingEOD"),
          onLoadText: t("sentToPrintClient")
        },
        data: {
          shipmentIds: shipmentIds.current.join(","),
          shipmentList: eod_ShipmentList,
          book: eod_Book,
          waybills: eod_Waybills,
          labels: eod_Labels,
        }
      })

      localStorage.setItem("eodOptions", JSON.stringify({
        book: eod_Book,
        waybills: eod_Waybills,
        labels: eod_Labels,
        shipmentList: eod_ShipmentList
      }))
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    setStatuses([
      { value: "10", name: t("open") },
      { value: "20", name: t("inProgress") },
      { value: "[12]0", name: t("openAndInProgress") },
      { value: "80", name: t("finished") },
      { value: "90", name: t("cancelled") },
    ])

    if ((location?.state as any)?.dialog !== undefined) {
      setConfirmDialog({ ...dialogDefault, ...(location?.state as any)?.dialog })
      window.history.replaceState({}, document.title)
    }

    api.get({
      endpoint: "/deliveryTypes",
      mount: null,
      params: {
        pageSize: 999,
        page: 1
      }
    }).then(res => setDeliveryTypes(res.data.data))
    api.get({
      endpoint: "/carriers",
      mount: null
    }).then(res => setCarriers(res.data))

    searchShipment(null, true)

    const eodOptions = localStorage.getItem("eodOptions")
    if (eodOptions) {
      const parsed = JSON.parse(eodOptions) as {
        book?: string,
        waybills?: string,
        labels?: string,
        shipmentList?: string
      }

      if (EOD_BOOK_OPTIONS.includes(parsed.book as any)) set_eod_Book(parsed.book as any)
      if (EOD_WAYBILL_OPTIONS.includes(parsed.waybills as any)) set_eod_Waybills(parsed.waybills as any)
      if (EOD_LABEL_OPTIONS.includes(parsed.labels as any)) set_eod_Labels(parsed.labels as any)
      if (EOD_SHIPMENT_LIST_OPTIONS.includes(parsed.shipmentList as any)) set_eod_ShipmentList(parsed.shipmentList as any)
    }
  }, [])

  const props = (key: keyof typeof searchFields) => {
    return {
      value: searchFields[key],
      onChange: (e: any) => setSearchField(e.target.value != "" ? e.target.value : undefined, key),
      onBlur: (e: any) => {
        if (e.target.value.trim().length === 0)
          setSearchField(undefined, key)

        if (key.toLocaleLowerCase().indexOf("date") > 0) {
          if (key === "dispDate") {
            let obj = { ...searchFields }
            delete obj.dispDateFrom
            delete obj.dispDateTo
            setSearchFields(obj)
          } else setSearchField(undefined, "dispDate")
        }
      } //if empty space, set undefined
    }
  }

  const getStatusElementClass = (status: number) => {
    let c = 'bg-gray-200 text-gray-600'
    if (status === 10) c = 'bg-gray-200 text-gray-600'
    else if (status === 20) c = 'bg-yellow-200 text-yellow-600'
    else if (status === 50) c = 'bg-blue-200 text-blue-600'
    else if (status === 70) c = 'bg-gray-400 text-gray-700'
    else if (status === 80) c = 'bg-green-200 text-green-600'
    else if (status === 90) c = 'bg-orange-200 text-orange-600'
    else if (status === -99) c = 'bg-red-300 text-red-600'

    return `status flex content-center py-1 px-2 rounded font-medium text-center ${c}`
  }

  return (
    <>
      {!newShipmentForm ? <>
        <Centralizer>
          <Container style={{ height: "70vh", maxHeight: "70vh", width: "clamp(0%, 100%, 1400px)" }}>
            <Group label={t("searchCriteria")}>
              <form onSubmit={(e) => searchShipment(e)}>
                <Grid>
                  <TextField {...props("trackingNo")}
                    title={t("enterConsOrPackageNo")}>{t("trackingNumber")}</TextField>
                  <TextField {...props("orderNumber")} id="shipment-orderno">{t("orderNumber")}</TextField>
                  <TextField {...props("receiver")}>{t("receiver")}</TextField>
                  <Select
                    onChange={(e) => setSearchField(e.target.value != "" ? e.target.value : undefined, "deliveryType")}
                    value={searchFields.deliveryType ?? ""}
                    data={deliveryTypes}
                    label={t("deliveryType")}
                    valueKey='name'
                    isDatalist={true}
                  />
                  {/* <TextField {...props("deliveryType")} id="shipment-deliveryType">{t("deliveryType")}</TextField> */}
                  {/* <TextField {...props("carrier")} id="shipment-carrier">{t("carrier")}</TextField> */}
                  <Select
                    onChange={(e) => setSearchField(e.target.value != "" ? e.target.value : undefined, "carrier")}
                    value={searchFields.carrier ?? ""}
                    data={carriers}
                    label={t("carrier")}
                    valueKey='name'
                    isDatalist={true}
                  />
                  <Select {...props("status")}
                    data={statuses as []}
                    label={t("status")}
                  />
                  <TextField {...props("shipmentId")} id="shipment-shipmentId">{t("shipmentId")}</TextField>
                  <div className="w-full md:w-fit xl:w-full">
                    {!showInterval ?
                      <Grid>
                        <TextField {...props("dispDate")}
                          _type="date"
                          required={eodMode !== "off"}
                        >{t("dispDate")}</TextField>
                      </Grid>
                      :
                      <>
                        <Grid>
                          <TextField {...props("dispDateFrom")} _type="date" required={eodMode !== "off"}>{t("dispDateFrom")}</TextField>
                          <TextField {...props("dispDateTo")} _type="date" required={eodMode !== "off"}>{t("dispDateTo")}</TextField>
                        </Grid>
                      </>
                    }
                  </div>
                </Grid>

                <Switch size='mini' onChange={() => setShowInterval(!showInterval)} checked={showInterval} label={t("showInterval")} />

                <Grid>
                  <Button className='w-full md:w-auto'>{t("search")}</Button>
                  <Button _type='button' className='w-full md:w-auto' onClick={() => { setSearchFields({}) }}>{t("clearsearch")}</Button>
                  <Button onClick={() => setShowSearchTips(true)} _type='button' variant='default'>{t("shipmentsSearch.link") + "..."}</Button>
                </Grid>
              </form>
            </Group>
            <div className='w-full flex justify-end'>
              {
                eodMode === "on" &&
                <Button
                  onClick={() => {
                    shipmentIds.current = Array.from(document.querySelectorAll("#shipmentsDataTable .table-select-checkbox:checked"))
                      .map((cb: Element) => cb.getAttribute("data-id")) as string[]

                    if (!shipmentIds.current.length) return toast.warning(t("youHaventChosenAnyShipments"))

                    setEODMode("dialog")
                  }}
                  variant="default"
                >{t("print")}</Button>
              }
              <Switch
                checked={eodMode !== "off"}
                onChange={() => setEODMode(v => v === "off" ? "on" : "off")}
                label={t("endOfDay")}
              />
              <Button onClick={() => setNewShipmentForm(true)} variant="add" id="CreateShipment">{t("createNewShipment")}</Button>
            </div>
            <>
              {eodMode === "off" ?
                <DataTable
                  fetchData={fetchShipments}
                  pageSize={PAGE_SIZE}
                  hideColumns={["shipmentId"]}
                  style={{ maxHeight: "30rem" }}
                  rerender={triggerReload}
                  openCallback={async (i: number, s: ShipmentType) => {
                    navigate(`/Shipment/${s.shipmentId}`)
                  }}
                  id="shipmentsDataTable"
                />
                :
                <DataTable
                  fetchData={fetchShipments}
                  hideColumns={["shipmentId"]}
                  style={{ maxHeight: "30rem" }}
                  rerender={triggerReload}
                  pageSize={-1}
                  selectId='shipmentId'
                  id="shipmentsDataTable"
                />
              }

            </>
          </Container>
        </Centralizer>
      </>
        :
        <>
          <Button variant='delete' className='absolute z-10 schipt-btn secondary'
            onClick={() => setNewShipmentForm(false)}>{t("goBack")}
          </Button>
          <Shipment />
        </>
      }
      {eodMode == "dialog" &&
        <Dialog >
          <Form onSubmit={async () => {
            setEODMode("on");
            endOfDay()
          }}>
            <Grid>
              <Switch
                label={t("shipmentList_s")}
                checked={eod_ShipmentList != "noPrint"}
                title={t("whatIsShipmentList")}
                onChange={() => set_eod_ShipmentList(v => eod_ShipmentList != "noPrint" ? "noPrint" : "forcePrint")}
              />
              {/* { eod_ShipmentList === "noPrint"  ? <></> :
                      <Switch 
                        label={t("eod.allowReprint")}
                        checked={eod_ShipmentList == "forcePrint"}                    
                        onChange={() => set_eod_ShipmentList(v => eod_ShipmentList != "forcePrint" ? "forcePrint" : "unprinted")}
                      />
                  }  */}
            </Grid>
            <Group label={t("eod.printDocuments")}>
              <Grid>
                <Switch
                  label={t("eod.bookUnbooked")}
                  checked={eod_Book != "noPrint"}
                  onChange={() => set_eod_Book(v => eod_Book != "noPrint" ? "noPrint" : "unprinted")}
                />
              </Grid>
              <Grid>
                <Switch
                  label={t("waybills")}
                  checked={eod_Waybills != "noPrint"}
                  onChange={() => set_eod_Waybills(v => eod_Waybills != "noPrint" ? "noPrint" : "unprinted")}
                />
                {eod_Waybills === "noPrint" ? <></> :
                  <Switch
                    label={t("eod.allowReprint")}
                    checked={eod_Waybills == "forcePrint"}
                    onChange={() => set_eod_Waybills(v => eod_Waybills != "forcePrint" ? "forcePrint" : "unprinted")}
                  />
                }
              </Grid>
              <Grid>
                <Switch
                  label={t("labels")}
                  checked={eod_Labels != "noPrint"}
                  onChange={() => set_eod_Labels(v => eod_Labels != "noPrint" ? "noPrint" : "unprinted")}
                />
                {eod_Labels === "noPrint" ? <></> :
                  <Switch
                    label={t("eod.allowReprint")}
                    checked={eod_Labels == "forcePrint"}
                    onChange={() => set_eod_Labels(v => eod_Labels != "forcePrint" ? "forcePrint" : "unprinted")}
                  />
                }
              </Grid>
            </Group>
            <Container>
              <Button variant='add' _type='submit'
                disabled={eod_Book === "noPrint" && eod_Labels === "noPrint" && eod_Waybills === "noPrint" && eod_ShipmentList === "noPrint"}
              >{t("continue") + "..."}</Button>
              <Button variant='undo' _type='button' onClick={() => setEODMode("on")}>{t("cancel")}</Button>
            </Container>
          </Form>
        </Dialog>
      }
      {
        showSearchTips &&
        <Dialog>
          <Container id='shipmentsSearchInfo'>
            <h2>{t("shipmentsSearch.mainInfo")}</h2>
            <p>{t("shipmentsSearch.example0")}</p>
            <table>
              <tbody>
                <tr><td>{t("shipmentsSearch.example1").split(" | ")[0]}</td><td><FaArrowRight /></td><td>{t("shipmentsSearch.example1").split(" | ")[1]}</td></tr>
                <tr><td>{t("shipmentsSearch.example2").split(" | ")[0]}</td><td><FaArrowRight /></td><td>{t("shipmentsSearch.example2").split(" | ")[1]}</td></tr>
                <tr><td>{t("shipmentsSearch.example3").split(" | ")[0]}</td><td><FaArrowRight /></td><td>{t("shipmentsSearch.example3").split(" | ")[1]}</td></tr>
                <tr><td>{t("shipmentsSearch.example4").split(" | ")[0]}</td><td><FaArrowRight /></td><td>{t("shipmentsSearch.example4").split(" | ")[1]}</td></tr>
              </tbody>
            </table>
            <Button variant='default' _type='button' onClick={() => setShowSearchTips(false)} >{t("ok")}</Button>
          </Container>
        </Dialog>
      }

      <ConfirmDialog options={confirmDialog as ConfirmDialogProps} />
    </>
  );
}